import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { CaretLeft, Play, Stop, Trash, WhatsappLogo, Warning, Timer as TimerIcon, CalendarCheck, X } from "@phosphor-icons/react";

const KEY = "hallogbs_kontraksi_v1";
const TOGGLE_LOCK_MS = 700;

const loadState = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY));
    if (raw && Array.isArray(raw.contractions)) {
      return { contractions: raw.contractions, activeStart: typeof raw.activeStart === "number" ? raw.activeStart : null };
    }
  } catch (e) { /* ignore */ }
  return { contractions: [], activeStart: null };
};

const pad = (n) => String(n).padStart(2, "0");
const fmtDur = (ms) => {
  if (ms == null || ms < 0) return "–";
  const s = Math.floor(ms / 1000);
  return `${pad(Math.floor(s / 60))}:${pad(s % 60)}`;
};
const fmtGap = (ms) => {
  if (ms == null || ms < 0) return "–";
  const totalMin = Math.floor(ms / 60000);
  const sec = Math.floor((ms % 60000) / 1000);
  return `${totalMin} mnt ${pad(sec)} dtk`;
};
const fmtClock = (t) => new Date(t).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

const Stat = ({ label, value, testid, accent }) => (
  <div className="rounded-2xl border border-pink-100 bg-white p-3 text-center shadow-[0_8px_20px_-16px_rgba(124,77,224,0.6)]">
    <div className={`font-heading text-xl font-extrabold ${accent ? "text-[#DB2777]" : "text-[#4C1D95]"}`} data-testid={testid}>{value}</div>
    <div className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
  </div>
);

// Normalize Indonesian WhatsApp numbers: 08.. / 62.. / +62.. -> 62XXXXXXXXXX. Returns "" if invalid.
const normalizeWa = (raw) => {
  const d = String(raw || "").replace(/\D/g, "");
  let n = "";
  if (d.startsWith("0")) n = "62" + d.slice(1);
  else if (d.startsWith("62")) n = d;
  else if (d.startsWith("8")) n = "62" + d;
  return /^62\d{9,13}$/.test(n) ? n : "";
};

export default function ContractionTimer() {
  const [state, setState] = useState(loadState);
  const [now, setNow] = useState(Date.now());
  const [locked, setLocked] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clinicWa, setClinicWa] = useState(undefined); // undefined=loading, ""/null=unavailable
  const lockTimer = useRef(null);
  const lockedRef = useRef(false);

  const { contractions, activeStart } = state;
  const running = activeStart != null;

  useEffect(() => {
    document.title = "Timer Kontraksi — HalloGBS";
    axios.get(`${API}/public/settings`)
      .then((r) => setClinicWa(normalizeWa(r.data?.clinic_wa)))
      .catch(() => setClinicWa(""));
  }, []);

  // Persist to localStorage on every change (timer data only; no server writes)
  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(state)); } catch (e) { /* ignore */ }
  }, [state]);

  // Tick from real clock while running (accurate across background/reload/screen-off)
  useEffect(() => {
    if (!running) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [running]);

  useEffect(() => () => { if (lockTimer.current) clearTimeout(lockTimer.current); }, []);

  const toggle = useCallback(() => {
    if (lockedRef.current) return; // synchronous guard: blocks double-tap even within one JS task
    lockedRef.current = true;
    setLocked(true);
    lockTimer.current = setTimeout(() => { lockedRef.current = false; setLocked(false); }, TOGGLE_LOCK_MS);
    const t = Date.now();
    setState((prev) => {
      if (prev.activeStart != null) {
        const rec = { start: prev.activeStart, end: t };
        return { contractions: [...prev.contractions, rec], activeStart: null };
      }
      return { ...prev, activeStart: t };
    });
    setNow(t);
  }, []);

  const doClear = useCallback(() => {
    setState({ contractions: [], activeStart: null });
    setConfirmClear(false);
  }, []);

  // Derived metrics
  const durations = contractions.map((c) => c.end - c.start);
  const gaps = contractions.slice(1).map((c, i) => c.start - contractions[i].start); // start-to-start
  const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);
  const avgDur = avg(durations);
  const avgGap = avg(gaps);
  const last = contractions[contractions.length - 1];
  const activeElapsed = running ? now - activeStart : 0;

  const recent = contractions
    .map((c, i) => ({ ...c, no: i + 1, gap: i > 0 ? c.start - contractions[i - 1].start : null }))
    .slice(-8)
    .reverse();

  return (
    <div className="min-h-screen bg-[#FAF7FF]" data-testid="contraction-timer-page">
      <header className="border-b border-rose-100 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-lg items-center justify-between px-4 py-3">
          <Link to="/artikel" data-testid="ct-back-articles" className="flex items-center gap-1.5 text-sm font-bold text-[#7C4DE0]"><CaretLeft size={16} weight="bold" /> Artikel &amp; Edukasi</Link>
          <span className="flex items-center gap-1.5 font-heading text-base font-extrabold text-[#4C1D95]"><TimerIcon size={18} weight="duotone" className="text-[#DB2777]" /> Timer Kontraksi</span>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-5">
        {/* Disclaimer */}
        <p data-testid="ct-disclaimer" className="mb-4 rounded-xl bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-800">
          Timer ini hanya membantu mencatat pola kontraksi dan bukan alat diagnosis. Kondisi persalinan harus dinilai oleh bidan atau dokter.
        </p>

        {/* Active timer / big button */}
        <section className={`rounded-3xl border p-6 text-center transition-colors ${running ? "border-rose-300 bg-gradient-to-br from-[#FFE4EF] to-[#FBD5E8]" : "border-pink-100 bg-white"}`}>
          <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-400">{running ? "Kontraksi berjalan" : "Timer aktif"}</div>
          <div className="flex items-center justify-center gap-2">
            {running && <span className="inline-block h-3 w-3 animate-ping rounded-full bg-rose-500" aria-hidden="true" />}
            <span data-testid="ct-active-timer" className={`font-heading text-6xl font-black tabular-nums ${running ? "text-[#BE185D]" : "text-slate-300"}`}>
              {fmtDur(running ? activeElapsed : 0)}
            </span>
          </div>

          <button
            data-testid="ct-toggle-btn"
            onClick={toggle}
            disabled={locked}
            aria-label={running ? "Akhiri kontraksi dan catat" : "Mulai mencatat kontraksi"}
            className={`mt-5 flex w-full items-center justify-center gap-2 rounded-2xl px-6 py-5 font-heading text-xl font-extrabold text-white shadow-lg transition-transform duration-150 active:scale-[0.98] disabled:opacity-70 ${running ? "bg-gradient-to-r from-rose-600 to-pink-600" : "bg-gradient-to-r from-[#7C4DE0] to-[#DB2777]"}`}
          >
            {running ? <><Stop size={24} weight="fill" /> Kontraksi Selesai</> : <><Play size={24} weight="fill" /> Mulai Kontraksi</>}
          </button>
        </section>

        {/* Stats */}
        <section className="mt-4 grid grid-cols-2 gap-3">
          <Stat label="Kontraksi terakhir" value={last ? fmtDur(last.end - last.start) : "–"} testid="ct-last" accent />
          <Stat label="Jumlah kontraksi" value={contractions.length} testid="ct-count" />
          <Stat label="Rata-rata durasi" value={avgDur != null ? fmtDur(avgDur) : "–"} testid="ct-avg-duration" />
          <Stat label="Rata-rata jarak" value={avgGap != null ? fmtGap(avgGap) : "–"} testid="ct-avg-gap" />
        </section>

        {/* History */}
        <section className="mt-5">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="font-heading text-sm font-extrabold uppercase tracking-wide text-[#4C1D95]">Riwayat terbaru</h2>
            {contractions.length > 0 && (
              <button data-testid="ct-clear-btn" onClick={() => setConfirmClear(true)} className="flex items-center gap-1 rounded-lg bg-rose-50 px-2.5 py-1 text-xs font-bold text-rose-600 hover:bg-rose-100">
                <Trash size={14} weight="bold" /> Hapus Riwayat
              </button>
            )}
          </div>
          {contractions.length === 0 ? (
            <p data-testid="ct-history-empty" className="rounded-2xl border border-dashed border-pink-200 bg-white/60 py-8 text-center text-sm text-slate-400">
              Belum ada kontraksi tercatat. Tekan <b>Mulai Kontraksi</b> saat kontraksi dimulai.
            </p>
          ) : (
            <ul data-testid="ct-history" className="space-y-2">
              {recent.map((c) => (
                <li key={c.start} className="flex items-center justify-between rounded-2xl border border-pink-100 bg-white px-4 py-2.5 text-sm">
                  <span className="flex items-center gap-2">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#EEE6FF] text-xs font-extrabold text-[#7C4DE0]">{c.no}</span>
                    <span className="text-slate-500">{fmtClock(c.start)}</span>
                  </span>
                  <span className="flex items-center gap-4">
                    <span className="text-slate-700"><b className="text-[#DB2777]">{fmtDur(c.end - c.start)}</b> durasi</span>
                    <span className="text-slate-400">{c.gap != null ? `jarak ${fmtGap(c.gap)}` : "kontraksi awal"}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Warning card */}
        <section data-testid="ct-warning" className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 p-4">
          <div className="mb-1 flex items-center gap-2 font-heading text-sm font-extrabold text-rose-700"><Warning size={18} weight="fill" /> Jangan menunggu hasil timer</div>
          <p className="text-xs leading-relaxed text-rose-700/90">
            Segera hubungi bidan atau fasilitas kesehatan apabila ketuban pecah/merembes, terjadi perdarahan, gerakan bayi berkurang, usia kehamilan belum 37 minggu, nyeri berat terus-menerus, atau ada dorongan kuat untuk mengejan.
          </p>
        </section>

        {/* Actions */}
        <section className="mt-4 space-y-2">
          {clinicWa ? (
            <a
              data-testid="ct-contact-gbs"
              href={`https://wa.me/${clinicWa}`}
              target="_blank"
              rel="noreferrer"
              aria-label="Hubungi GBS melalui WhatsApp"
              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-500 px-5 py-3.5 font-heading text-base font-extrabold text-white shadow-md transition-colors hover:bg-emerald-600"
            >
              <WhatsappLogo size={20} weight="fill" /> Hubungi GBS
            </a>
          ) : clinicWa === "" ? (
            <div data-testid="ct-contact-unavailable" className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-center text-xs text-slate-500">
              Nomor kontak klinik belum tersedia. Silakan hubungi GBS melalui halaman reservasi.
            </div>
          ) : null}

          <Link
            to="/booking"
            data-testid="ct-reservation-link"
            className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-[#7C4DE0] bg-white px-5 py-3 font-heading text-base font-extrabold text-[#7C4DE0] transition-colors hover:bg-[#F5F0FF]"
          >
            <CalendarCheck size={20} weight="duotone" /> Buat Reservasi GBS
          </Link>
        </section>

        <p className="mt-5 text-center text-[11px] text-slate-400">Data timer disimpan hanya di perangkat Anda dan tidak dikirim ke mana pun.</p>
      </main>

      {/* Confirm clear dialog */}
      {confirmClear && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={() => setConfirmClear(false)}>
          <div data-testid="ct-clear-dialog" onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-heading text-lg font-extrabold text-[#4C1D95]">Hapus semua riwayat?</h3>
              <button data-testid="ct-clear-dialog-close" onClick={() => setConfirmClear(false)} className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500"><X size={18} weight="bold" /></button>
            </div>
            <p className="mb-5 text-sm text-slate-500">Seluruh catatan kontraksi di perangkat ini akan dihapus permanen dan tidak dapat dikembalikan.</p>
            <div className="flex gap-3">
              <button data-testid="ct-clear-cancel" onClick={() => setConfirmClear(false)} className="flex-1 rounded-xl bg-slate-100 py-3 text-sm font-bold text-slate-600">Batal</button>
              <button data-testid="ct-clear-confirm" onClick={doClear} className="flex-1 rounded-xl bg-rose-600 py-3 text-sm font-bold text-white hover:bg-rose-700">Ya, Hapus</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
